import { Clipboard } from '@angular/cdk/clipboard';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Note } from '@vsolv/packages/notes/domain';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Distributor } from '@wsphere/distributors/domain';
import { SftpService } from '@wsphere/sftp/web';
import { SecurityService, StaffService } from '@wsphere/staff/web';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  ArchiveDistributorDialog,
  CreateDistributorOnboardingLinkDialog,
  DeactivateDistributorDialog,
  ReactivateDistributorDialog,
  SelectedPageType,
} from '../../dialogs';
import { TabsComponent } from '@vsolv/vectors-ui/tabs';
import { BatchPaymentService, DistributorService } from '../../services';
import { DistributorPaymentHistoryPage } from '../payment-history/payment-history.page';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './distributor.page.html',
  standalone: false,
})
export class DistributorPage implements OnDestroy {
  constructor(
    private router: Router,
    private http: HttpClient,
    private sftpSvc: SftpService,
    private clipboard: Clipboard,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private staffSvc: StaffService,
    private portalSvc: PortalService,
    private securitySvc: SecurityService,
    private distributorSvc: DistributorService,
    private batchPaymentSvc: BatchPaymentService
  ) {}

  @ViewChild('tabs') private tabs?: TabsComponent;
  @ViewChild(DistributorPaymentHistoryPage) private paymentHistoryPage?: DistributorPaymentHistoryPage;
  @ViewChild('archiveDistributorDialog') archiveDistributor?: ArchiveDistributorDialog;
  @ViewChild('deactivateDistributorDialog') deactivateDistributor?: DeactivateDistributorDialog;
  @ViewChild('reactivateDistributorDialog') reactivateDistributor?: ReactivateDistributorDialog;
  @ViewChild('createLinkDialog') createLinkDialog?: CreateDistributorOnboardingLinkDialog;

  staff$ = from(this.staffSvc.retrieveSelf()).pipe(shareReplay(1));

  private distributorUpdated$ = new BehaviorSubject<Distributor.Model | null>(null);
  readonly distributor$ = combineLatest([this.route.data, this.distributorUpdated$]).pipe(
    map(([data]) => {
      const hideBreadcrumbs = data['settings'] as boolean;
      this.hideBreadcrumbs = hideBreadcrumbs;

      const distributor = data['distributor'] as Distributor.Model;
      return distributor;
    }),
    switchMap(distributor =>
      this.distributorUpdated$.pipe(map(updatedDistributor => updatedDistributor ?? distributor))
    )
  );

  protected loadingPinnedNotes$ = new BehaviorSubject(false);
  protected pinnedNotes$ = this.distributor$.pipe(
    map(distributor => distributor.id),
    distinctUntilChanged(),
    tap(() => this.loadingPinnedNotes$.next(true)),
    switchMap(distributorId => this.http.get<Note.Model[]>(`/api/distributors/${distributorId}/notes/pinned`)),
    catchError(() => []),
    tap(() => setTimeout(() => this.loadingPinnedNotes$.next(false)))
  );

  readonly checkoutLink$ = this.distributor$.pipe(
    switchMap(async distributor => {
      const { url } = await this.portalSvc.getPortalUrl();
      return `${url}/checkout?distributorId=${distributor.id}`;
    })
  );

  readonly canOnboardDistributor$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(
      async ([distributor]) =>
        await this.securitySvc.hasAccess(
          'dist_OnboardDistributor',
          distributor?.permissionKey ? [distributor.permissionKey] : null
        )
    )
  );

  readonly canEditDistributor$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(
      async ([distributor]) =>
        await this.securitySvc.hasAccess(
          'dist_EditDetails',
          distributor.permissionKey ? [distributor.permissionKey] : null
        )
    )
  );

  readonly canViewDistributor$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(
      async ([distributor]) =>
        await this.securitySvc.hasAccess(
          'dist_ViewDistributor',
          distributor.permissionKey ? [distributor.permissionKey] : null
        )
    )
  );

  readonly canUpdateStatus$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) =>
      this.securitySvc.hasAccess('dist_EditStatus', distributor?.permissionKey ? [distributor.permissionKey] : null)
    )
  );

  readonly canViewStaff$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) =>
      this.securitySvc.hasAccess('dist_ViewStaff', distributor?.permissionKey ? [distributor.permissionKey] : null)
    )
  );

  protected canViewDocuments$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) =>
      this.securitySvc.hasAccess('dist_ViewDocuments', distributor?.permissionKey ? [distributor.permissionKey] : null)
    )
  );

  protected canViewCommission$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) =>
      this.securitySvc.hasAccess('dist_ViewCommission', distributor?.permissionKey ? [distributor.permissionKey] : null)
    )
  );

  protected canManageAccountInfo$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) =>
      this.securitySvc.hasAccess(
        'dist_ManageAccountInfo',
        distributor?.permissionKey ? [distributor.permissionKey] : null
      )
    )
  );

  // Batch payments are opt-in per tenant. Gating the tab hides the whole flow — list, selection and
  // payment — since nothing else links to it.
  protected canViewWarranties$: Observable<boolean> = combineLatest([
    this.distributor$,
    this.staff$,
    this.batchPaymentSvc.isEnabled$,
  ]).pipe(
    switchMap(async ([distributor, , batchPaymentsEnabled]) => {
      if (!batchPaymentsEnabled) return false;

      return this.securitySvc.hasAccess(
        'wrt_ViewWarranty',
        distributor?.permissionKey ? [distributor.permissionKey] : null
      );
    })
  );

  // Mirrors the ledger-statement endpoint's own gate (RetrieveDistributorLedgerStatementQueryController):
  // financial detail needs both "can see this distributor" and "can see sales/financial data".
  protected canViewLedgerStatement$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) => {
      const permissionKeys = distributor?.permissionKey ? [distributor.permissionKey] : null;
      const [canViewDistributor, canViewSales] = await Promise.all([
        this.securitySvc.hasAccess('dist_ViewDistributor', permissionKeys),
        this.securitySvc.hasAccess('py_ViewSales', permissionKeys),
      ]);
      return canViewDistributor && canViewSales;
    })
  );

  readonly sftpEnabled$ = this.distributor$.pipe(
    switchMap(async dist =>
      dist && dist.status !== Distributor.Status.ARCHIVED
        ? (await this.sftpSvc.getSettings())?.distributorEnabled
        : false
    )
  );

  hideBreadcrumbs = true;

  readonly archived = Distributor.Status.ARCHIVED;
  readonly activated = Distributor.Status.ACTIVE;
  readonly deactivated = Distributor.Status.DEACTIVATED;

  manageLinks = SelectedPageType.MANAGE_LINKS;

  async refreshDistributor(distributorId: string) {
    const distributor = await this.distributorSvc.getOne(distributorId);
    this.distributorUpdated$.next(distributor);
  }

  // The history table fetches once and stays mounted - refetch when its tab opens.
  protected onTabChanged() {
    if (this.tabs?.selectedTab?.config?.label === 'Payment History') this.paymentHistoryPage?.refresh();
  }

  navigateTo(distributorId?: string) {
    if (distributorId) this.router.navigate([`../${distributorId}`], { relativeTo: this.route });
    else this.router.navigate([`../`], { relativeTo: this.route });
  }

  copyCheckoukLink(checkoutLink: string | null) {
    if (checkoutLink) {
      this.clipboard.copy(checkoutLink);
      this.toastSvc.show({
        type: 'success',
        title: 'Copied to clipboard!',
        text: `Link copied to clipboard or <a class="text-primary" href="${checkoutLink}">click here</a>.`,
      });
    }
  }

  ngOnDestroy(): void {
    this.distributorUpdated$.complete();
    this.loadingPinnedNotes$.complete();
  }
}
